import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import DOMPurify from 'dompurify'
import { supabase, getSessionSafe } from '../lib/supabase'
import { dbFetch } from '../lib/dbFetch'
import useAuthStore from './authStore'
import useTaskStore from './taskStore'

const sanitize = (str) => typeof str === 'string'
  ? DOMPurify.sanitize(str, { ALLOWED_TAGS: [] })
  : str

// ── Storage helpers: raw fetch contro l'API REST di Supabase Storage. ─────
// Bypassa il client supabase-js per evitare il bug `navigator.locks` che
// blocca indefinitamente le chiamate `.storage.upload/remove/list` dopo
// background prolungato della tab. Stesso pattern del `dbFetch` wrapper
// usato per le mutazioni DB.

const STORAGE_BUCKET = 'practice-attachments'
const STORAGE_UPLOAD_TIMEOUT_MS = 60_000 // file fino a 20MB su rete lenta

async function rawStorageUpload(path, file, accessToken) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), STORAGE_UPLOAD_TIMEOUT_MS)
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${encodeURI(path)}`
    const res = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type':  file.type || 'application/octet-stream',
        'x-upsert':      'false',
      },
      body: file,
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || data.error || `HTTP ${res.status}`)
    }
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('upload_timeout')
    throw e
  } finally {
    clearTimeout(timeoutId)
  }
}

async function rawStorageRemove(path, accessToken) {
  // Best-effort: errori loggati ma non rilanciati (è una rollback)
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${encodeURI(path)}`
    await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    })
  } catch (e) {
    console.warn('[rawStorageRemove] failed', e?.message)
  }
}

// ── Mappers: snake_case DB → camelCase JS ───────────
const mapPractice = (p) => ({
  id:        p.id,
  title:     p.title,
  status:    p.status,
  notes:     p.notes ?? '',
  dueDate:   p.due_date,
  createdAt: p.created_at,
  updatedAt: p.updated_at,
})

const toDbPractice = (data) => ({
  title:    sanitize(data.title),
  status:   data.status ?? 'open',
  notes:    sanitize(data.notes ?? '') ?? '',
  due_date: data.dueDate || null,
})

const byDueDate = (a, b) => {
  if (!a.dueDate && !b.dueDate) return 0
  if (!a.dueDate) return 1
  if (!b.dueDate) return -1
  return a.dueDate.localeCompare(b.dueDate)
}

// ── Store ───────────────────────────────────────────
const mapDocument = (d) => ({
  id:          d.id,
  practiceId:  d.practice_id,
  storagePath: d.storage_path,
  filename:    d.filename,
  mimeType:    d.mime_type,
  sizeBytes:   d.size_bytes,
  aiParsed:    d.ai_parsed,
  aiSummary:   d.ai_summary,
  createdAt:   d.created_at,
})

const usePracticeStore = create(
  persist(
    (set, get) => ({
      practices: null,
      documents: null,
      _channel:  null,
      _docsChannel: null,
      syncing:   false,

      fetchPractices: async () => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) return
        set({ syncing: true })
        try {
          const result = await Promise.race([
            supabase.from('practices').select('*').eq('user_id', userId).order('due_date', { ascending: true, nullsFirst: false }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('fetchPractices timeout')), 5000)),
          ])
          set({ practices: (result?.data ?? []).map(mapPractice) })
        } catch (err) {
          console.warn('[practiceStore.fetchPractices]', err.message)
        } finally {
          set({ syncing: false })
        }
      },

      fetchDocuments: async () => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) return
        try {
          const result = await Promise.race([
            supabase.from('practice_documents').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('fetchDocuments timeout')), 5000)),
          ])
          set({ documents: (result?.data ?? []).map(mapDocument) })
        } catch (err) {
          console.warn('[practiceStore.fetchDocuments]', err.message)
        }
      },

      initRealtime: () => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) return
        get().fetchPractices()
        get().fetchDocuments()

        const channel = supabase
          .channel(`store-practices:${userId}`)
          .on('postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'practices', filter: `user_id=eq.${userId}` },
            ({ new: row }) => set(s => {
              const existing = s.practices ?? []
              const idx = existing.findIndex(p => p.id === row.id)
              const next = idx >= 0
                ? existing.map(p => p.id === row.id ? mapPractice(row) : p)
                : [...existing, mapPractice(row)]
              return { practices: next.sort(byDueDate) }
            })
          )
          .on('postgres_changes',
            { event: 'UPDATE', schema: 'public', table: 'practices', filter: `user_id=eq.${userId}` },
            ({ new: row }) => set(s => ({
              practices: (s.practices ?? []).map(p => p.id === row.id ? mapPractice(row) : p)
            }))
          )
          .on('postgres_changes',
            { event: 'DELETE', schema: 'public', table: 'practices', filter: `user_id=eq.${userId}` },
            ({ old: row }) => set(s => ({
              practices: (s.practices ?? []).filter(p => p.id !== row.id)
            }))
          )
          .subscribe()

        const docsChannel = supabase
          .channel(`store-practice-docs:${userId}`)
          .on('postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'practice_documents', filter: `user_id=eq.${userId}` },
            ({ new: row }) => set(s => {
              const existing = s.documents ?? []
              const idx = existing.findIndex(d => d.id === row.id)
              const next = idx >= 0
                ? existing.map(d => d.id === row.id ? mapDocument(row) : d)
                : [mapDocument(row), ...existing]
              return { documents: next }
            })
          )
          .on('postgres_changes',
            { event: 'DELETE', schema: 'public', table: 'practice_documents', filter: `user_id=eq.${userId}` },
            ({ old: row }) => set(s => ({
              documents: (s.documents ?? []).filter(d => d.id !== row.id)
            }))
          )
          .subscribe()

        set({ _channel: channel, _docsChannel: docsChannel })
      },

      destroyRealtime: () => {
        const { _channel, _docsChannel } = get()
        if (_channel)     supabase.removeChannel(_channel)
        if (_docsChannel) supabase.removeChannel(_docsChannel)
        set({ practices: null, documents: null, _channel: null, _docsChannel: null })
      },

      // ── CRUD ──
      // Restituisce l'id della pratica creata (utile per il flusso pdf-to-practice
      // che dopo crea i task con practice_id già pronto).
      addPractice: async (data) => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) throw new Error('Not authenticated')
        const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`
        const now = new Date().toISOString()
        const optimistic = mapPractice({
          id,
          ...toDbPractice(data),
          user_id:    userId,
          created_at: now,
          updated_at: now,
        })
        set(s => ({ practices: [...(s.practices ?? []), optimistic].sort(byDueDate) }))
        try {
          await dbFetch('practices', 'POST', { id, ...toDbPractice(data), user_id: userId })
          return id
        } catch (err) {
          set(s => ({ practices: (s.practices ?? []).filter(p => p.id !== id) }))
          throw err
        }
      },

      updatePractice: async (id, data) => {
        const prev = get().practices?.find(p => p.id === id)
        if (!prev) return
        set(s => ({
          practices: (s.practices ?? []).map(p => p.id === id ? { ...p, ...data } : p),
        }))
        try {
          await dbFetch(`practices?id=eq.${id}`, 'PATCH', toDbPractice({ ...prev, ...data }))
        } catch (err) {
          set(s => ({
            practices: (s.practices ?? []).map(p => p.id === id ? prev : p),
          }))
          throw err
        }
      },

      deletePractice: async (id) => {
        const userId = useAuthStore.getState().user?.id
        // Cleanup file in Storage prima del DELETE — la FK ON DELETE CASCADE
        // pulirà le righe in practice_documents, ma i file su Storage restano
        // orfani se non li tocchiamo qui. Best-effort: errori loggati ma non
        // bloccano l'eliminazione della pratica.
        if (userId) {
          try {
            const { data: files } = await supabase.storage
              .from('practice-attachments')
              .list(`${userId}/${id}`)
            if (files?.length) {
              const paths = files.map(f => `${userId}/${id}/${f.name}`)
              await supabase.storage.from('practice-attachments').remove(paths)
            }
          } catch (e) {
            console.warn('[deletePractice] storage cleanup failed', e)
          }
        }
        await dbFetch(`practices?id=eq.${id}`, 'DELETE')
        set(s => ({ practices: (s.practices ?? []).filter(p => p.id !== id) }))
      },

      // Upload diretto via raw fetch (NON supabase-js per evitare il bug
      // navigator.locks che hanga le chiamate .storage.upload dopo background
      // prolungato della tab — stessa ragione per cui dbFetch esiste per le
      // mutation DB). RLS policy bucket garantisce ownership.
      // opts: { aiParsed?: boolean, aiSummary?: string | null }
      uploadDocument: async (practiceId, file, opts = {}) => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) throw new Error('Not authenticated')

        // Token con timeout: anche se il refresh sessione è bloccato,
        // getSessionSafe ritorna la cached entro 2s.
        const session = await getSessionSafe()
        if (!session?.access_token) throw new Error('Not authenticated')

        // Estensione derivata dal MIME (sicuro), non dal filename utente.
        // Whitelist allineata al bucket allowed_mime_types.
        const EXT_BY_MIME = {
          'application/pdf': 'pdf',
          'image/jpeg':      'jpg',
          'image/png':       'png',
          'image/webp':      'webp',
          'image/heic':      'heic',
          'image/heif':      'heif',
        }
        const ext = EXT_BY_MIME[file.type] || 'bin'

        // Filename "umano" sanitizzato prima di scrivere su DB. Strip:
        //  - control chars (\x00-\x1f)
        //  - separatori filesystem (< > : " / \ | ? *)
        //  - U+202E (RIGHT-TO-LEFT OVERRIDE, vettore tipico di filename spoofing)
        const safeFilename = (file.name || `file.${ext}`)
          // eslint-disable-next-line no-control-regex
          .replace(/[\x00-\x1f<>:"/\\|?*‮]/g, '_')
          .slice(0, 255)

        const docId = crypto.randomUUID()
        const path  = `${userId}/${practiceId}/${docId}.${ext}`

        // Upload via raw fetch + AbortController (timeout 60s)
        await rawStorageUpload(path, file, session.access_token)

        // Mutation via dbFetch (raw fetch) per evitare navigator.locks hang di supabase-js
        try {
          await dbFetch('practice_documents', 'POST', {
            id: docId,
            practice_id: practiceId,
            user_id: userId,
            storage_path: path,
            filename: safeFilename,
            mime_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
            ai_parsed: opts.aiParsed ?? false,
            ai_summary: opts.aiSummary ?? null,
          })
        } catch (insErr) {
          // Rollback via raw delete (anche questo non passa per supabase-js)
          await rawStorageRemove(path, session.access_token)
          throw insErr
        }
        return docId
      },

      // Commit del flusso PDF→Pratica eseguito interamente client-side via
      // supabase-js (RLS-secured). Evitiamo il giro per l'edge function che
      // andrebbe in timeout Cloudflare con N file in base64 nel body.
      //   args: { targetPracticeId?: string, practice: {title, dueDate, summary}, tasks: [...], files: File[] }
      // Ritorna: { practiceId, docsCount, tasksCount }
      commitPdfPractice: async ({ targetPracticeId, practice, tasks, files }) => {
        const userId = useAuthStore.getState().user?.id
        if (!userId) throw new Error('Not authenticated')

        // 1. Pratica: usa esistente o creane una nuova
        let practiceId = targetPracticeId
        if (!practiceId) {
          practiceId = await get().addPractice({
            title: practice.title.trim(),
            dueDate: practice.dueDate || '',
            notes: practice.summary || '',
          })
        }

        // 2. Upload allegati in parallelo (best-effort, errori isolati)
        const aiSummary = practice.summary || null
        const docResults = await Promise.allSettled(
          (files || []).map(f => get().uploadDocument(practiceId, f, {
            aiParsed: true, aiSummary,
          }))
        )
        const docsCount = docResults.filter(r => r.status === 'fulfilled').length

        // 3. Inserimento tasks via taskStore.addTask (gestisce optimistic + RLS)
        const taskAdd = useTaskStore.getState().addTask
        let tasksCount = 0
        for (const task of tasks || []) {
          if (!task.title?.trim()) continue
          try {
            await taskAdd({
              title:           task.title.trim(),
              category:        task.category || 'altro',
              dueDate:         task.dueDate || '',
              dueTime:         task.dueTime || '',
              priority:        task.priority || 'media',
              notes:           task.notes || '',
              reminder:        task.reminder ?? (task.dueDate && task.dueTime ? true : false),
              reminderMinutes: task.reminderMinutes ?? 15,
              practiceId,
            })
            tasksCount++
          } catch (e) {
            console.error('[commitPdfPractice] task failed', task.title, e)
          }
        }

        return { practiceId, docsCount, tasksCount }
      },

      // Rinomina solo il filename "umano" (label). Lo storage_path resta
      // invariato (è basato su docId + ext, non sul filename utente).
      renameDocument: async (docId, newName) => {
        const prev = get().documents?.find(d => d.id === docId)
        if (!prev) return
        const trimmed = (newName || '').trim()
        if (!trimmed) throw new Error('empty_filename')
        // Stesso sanitize dell'upload: strip control chars + separatori FS + RTL override
        const safe = trimmed
          // eslint-disable-next-line no-control-regex
          .replace(/[\x00-\x1f<>:"/\\|?*\u202E]/g, '_')
          .slice(0, 255)
        if (safe === prev.filename) return
        // Optimistic
        set(s => ({
          documents: (s.documents ?? []).map(d => d.id === docId ? { ...d, filename: safe } : d),
        }))
        try {
          await dbFetch(`practice_documents?id=eq.${docId}`, 'PATCH', { filename: safe })
        } catch (err) {
          // Rollback
          set(s => ({
            documents: (s.documents ?? []).map(d => d.id === docId ? prev : d),
          }))
          throw err
        }
      },

      deleteDocument: async (docId) => {
        const doc = get().documents?.find(d => d.id === docId)
        if (!doc) return
        try {
          await supabase.storage.from('practice-attachments').remove([doc.storagePath])
        } catch (e) {
          console.warn('[deleteDocument] storage remove failed', e)
        }
        await dbFetch(`practice_documents?id=eq.${docId}`, 'DELETE')
        set(s => ({ documents: (s.documents ?? []).filter(d => d.id !== docId) }))
      },

      // Forza ri-estrazione del testo dai PDF della pratica (chiama edge function
      // refresh-practice-documents). Utile per:
      //   - retry su estrazioni fallite
      //   - pre-warm cache prima di una sessione di chat con l'agent
      // Premium-only lato server.
      refreshPracticeDocuments: async (practiceId) => {
        const session = await getSessionSafe()
        const token = session?.access_token
        if (!token) throw new Error('Not authenticated')
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refresh-practice-documents`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ practice_id: practiceId }),
          }
        )
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'refresh_failed')
        return json // { refreshed, total_pdfs, images_skipped, errors }
      },

      // Restituisce una signed URL temporanea (1h) per scaricare/visualizzare
      // un allegato. Bucket privato → serve sempre la URL firmata.
      //
      // Uso raw fetch + AbortController invece di supabase.storage.createSignedUrl
      // perché quest'ultima passa per il client supabase-js che ha il bug
      // navigator.locks: dopo un po' che la tab è aperta, può hangare
      // indefinitamente lasciando l'utente con una scheda "about:blank" infinita.
      // Stesso pattern del dbFetch wrapper.
      getDocumentUrl: async (storagePath) => {
        const session = await getSessionSafe()
        if (!session?.access_token) throw new Error('Not authenticated')

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 8000)
        try {
          const url = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/sign/practice-attachments/${encodeURI(storagePath)}`
          const res = await fetch(url, {
            method: 'POST',
            signal: controller.signal,
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type':  'application/json',
            },
            body: JSON.stringify({ expiresIn: 3600 }),
          })
          const data = await res.json().catch(() => ({}))
          if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
          // L'API ritorna signedURL relativa: "/object/sign/.../path?token=...".
          // Prependere base + /storage/v1 per costruire la URL assoluta navigabile.
          const rel = data.signedURL || data.signedUrl
          if (!rel) throw new Error('no_signed_url')
          return `${import.meta.env.VITE_SUPABASE_URL}/storage/v1${rel}`
        } catch (e) {
          if (e.name === 'AbortError') throw new Error('timeout')
          throw e
        } finally {
          clearTimeout(timeoutId)
        }
      },

      togglePracticeStatus: async (id) => {
        const prev = get().practices?.find(p => p.id === id)
        if (!prev) return
        const newStatus = prev.status === 'completed' ? 'open' : 'completed'
        set(s => ({
          practices: (s.practices ?? []).map(p => p.id === id ? { ...p, status: newStatus } : p),
        }))
        try {
          await dbFetch(`practices?id=eq.${id}`, 'PATCH', { status: newStatus })
        } catch (err) {
          set(s => ({
            practices: (s.practices ?? []).map(p => p.id === id ? prev : p),
          }))
          throw err
        }
        // Cascade: completare la pratica completa anche tutti i task collegati
        // ancora aperti. Riaprire la pratica NON tocca i task (l'utente potrebbe
        // averli completati manualmente, non vogliamo resettarli).
        if (newStatus === 'completed') {
          const allTasks = useTaskStore.getState().tasks ?? []
          const openLinked = allTasks.filter(t => t.practiceId === id && !t.completed)
          const toggleTask = useTaskStore.getState().toggleTask
          // Parallelo: ognuno fa il PATCH ottimistico, errori isolati per task
          await Promise.allSettled(openLinked.map(t => toggleTask(t.id)))
        }
      },
    }),
    {
      name: 'practice-data',
      partialize: (state) => ({ practices: state.practices, documents: state.documents }),
    }
  )
)

export default usePracticeStore
